import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Divider, MenuItem, Select, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { SimpleTreeView, TreeItem, treeItemClasses } from '@mui/x-tree-view';
import { WizardUtils } from '../../../../utils/WizardUtils';
import { CriteriaFormulaPreview, getFormulaConditionItemActions } from './components';

const CustomTreeItem = styled(TreeItem)(({ theme }) => ({
  [`& .${treeItemClasses.content}`]: {
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    margin: theme.spacing(0.2, 0),
    [`& .${treeItemClasses.label}`]: {
      fontSize: '0.8rem',
      fontWeight: 500,
    },
  },
  [`& .${treeItemClasses.groupTransition}`]: {
    marginLeft: 15,
    paddingLeft: 18,
    borderLeft: `1px dashed ${theme.palette.text.primary}`,
  },
  ...theme.applyStyles('light', {
    color: theme.palette.grey[800],
  }),
}));

const buildDefaultCriterion = (criteriaIds) => {
  if (criteriaIds.length === 0) return;

  const newCriterion = { type: 'criterion', id: criteriaIds[0] };
  WizardUtils.patchConditionTreeWithUID(newCriterion);
  return newCriterion;
};

const buildDefaultCondition = (criteriaIds, setExpandedItems, isRoot = false, defaultValue) => {
  const conditionFromDefaultValue = WizardUtils.isFormulaEmpty(defaultValue)
    ? null
    : WizardUtils.createTriggerConditionTreeFromString(defaultValue);

  WizardUtils.patchConditionTreeWithUID(conditionFromDefaultValue);
  if (conditionFromDefaultValue?.children != null) {
    conditionFromDefaultValue.uid = 'root';
    return conditionFromDefaultValue;
  }

  const newCriterion = buildDefaultCriterion(criteriaIds);
  const children = newCriterion != null ? [newCriterion] : [];

  const newCondition = { type: 'and', children };
  WizardUtils.patchConditionTreeWithUID(newCondition);
  if (isRoot) newCondition.uid = 'root';

  // Do not update expanded items list for root condition to prevent infinite loop on initialization
  if (!isRoot) setExpandedItems((previousExpandedItems) => [...previousExpandedItems, newCondition.uid]);

  return newCondition;
};

export const CriteriaFormulaEditor = ({ criteria, value: conditionString, onChange }) => {
  const previousConditionString = useRef(conditionString);
  const [expandedItems, setExpandedItems] = useState(['root']);
  const criteriaIds = useMemo(() => criteria.map((item) => item.Identifiant), [criteria]);

  const [conditionTree, setConditionTree] = useState(
    buildDefaultCondition(criteriaIds, setExpandedItems, true, conditionString)
  );

  // Update the component when the parent provides a new condition string value
  useEffect(() => {
    if (conditionString !== previousConditionString.current) {
      previousConditionString.current = conditionString;
      setConditionTree(buildDefaultCondition(criteriaIds, setExpandedItems, true, conditionString));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conditionString]);

  // Send new condition string to parent component when the condition tree is updated (through the "onChange" callback)
  useEffect(() => {
    const serializedCondition = WizardUtils.createTriggerConditionStringFromTree(conditionTree);
    if (serializedCondition !== previousConditionString.current) {
      previousConditionString.current = serializedCondition;
      onChange(serializedCondition);
    }
  }, [conditionTree, onChange]);

  const conditionTreeItems = useMemo(() => {
    const buildOperatorSelector = (elementId, conditionTree, onChange) => {
      const domElementId = `${elementId}-operator-selector`;
      return (
        <Select
          key={domElementId}
          id={domElementId}
          data-cy={domElementId}
          size="small"
          value={conditionTree.type}
          onChange={(event) => onChange({ ...conditionTree, type: event.target.value })}
          MenuProps={{ 'data-cy': `${domElementId}-options-container` }}
        >
          <MenuItem key="and" value="and">
            AND
          </MenuItem>
          <MenuItem key="or" value="or">
            OR
          </MenuItem>
        </Select>
      );
    };

    const buildCriterionSelector = (elementId, criterion, onChange) => {
      if (!criteriaIds.includes(criterion.id)) return null;
      const domElementId = `${elementId}-criterion-selector`;
      return (
        <Select
          key={domElementId}
          id={domElementId}
          data-cy={domElementId}
          size="small"
          value={criterion.id}
          onChange={(event) => onChange({ ...criterion, id: event.target.value })}
          MenuProps={{ 'data-cy': `${domElementId}-options-container` }}
        >
          {criteriaIds.map((listOption) => (
            <MenuItem key={listOption} value={listOption}>
              {listOption}
            </MenuItem>
          ))}
        </Select>
      );
    };

    const buildTreeElements = (conditionTree, elementId = 'root', onChange, onDelete) => {
      if (conditionTree == null) return null;

      const buildTreeItemLabel = (conditionElement) => {
        const onAddNewConditionGroup = (event) =>
          onChange({
            ...conditionTree,
            children: [...conditionTree.children, buildDefaultCondition(criteriaIds, setExpandedItems)],
          });

        const onAddNewCriterion = (event) => {
          const newCriterion = buildDefaultCriterion(criteriaIds);
          if (newCriterion != null) onChange({ ...conditionTree, children: [...conditionTree.children, newCriterion] });
        };

        const buttons = getFormulaConditionItemActions({
          conditionType: conditionTree.type,
          elementId,
          isRoot: elementId === 'root',
          onAddNewConditionGroup,
          onAddNewCriterion,
          onDelete,
        });

        let domElementId = `${elementId}-${conditionTree.type}`;
        if (conditionTree.type !== 'criterion') domElementId += '-group';

        return (
          <Stack
            key={domElementId}
            id={domElementId}
            data-cy={domElementId}
            direction="row"
            alignItems="center"
            gap={1}
          >
            {[conditionElement, ...buttons]}
          </Stack>
        );
      };

      if (conditionTree.type === 'criterion') {
        return (
          <CustomTreeItem
            key={conditionTree.uid}
            itemId={conditionTree.uid}
            label={buildTreeItemLabel(buildCriterionSelector(elementId, conditionTree, onChange))}
          />
        );
      }

      const deleteChildAtIndex = (index) => {
        const newChildren = [...conditionTree.children];
        newChildren.splice(index, 1);
        onChange({ ...conditionTree, children: newChildren });
      };

      const modifyChildAtIndex = (index, newChild) => {
        const newChildren = [...conditionTree.children];
        newChildren[index] = newChild;
        onChange({ ...conditionTree, children: newChildren });
      };

      return (
        <CustomTreeItem
          key={conditionTree.uid}
          itemId={conditionTree.uid}
          label={buildTreeItemLabel(buildOperatorSelector(elementId, conditionTree, onChange))}
        >
          {conditionTree.children.map((childCondition, index) => {
            const childElementIdPrefix = elementId === 'root' ? '' : `${elementId}::`;
            const childElementId = `${childElementIdPrefix}${index + 1}`;
            return buildTreeElements(
              childCondition,
              childElementId,
              (newChild) => modifyChildAtIndex(index, newChild),
              () => deleteChildAtIndex(index)
            );
          })}
        </CustomTreeItem>
      );
    };

    const elements = buildTreeElements(conditionTree, 'root', setConditionTree);
    return elements;
  }, [criteriaIds, conditionTree, setConditionTree]);

  const conditionTreeElement = useMemo(() => {
    return (
      <SimpleTreeView
        disableSelection
        expandedItems={expandedItems}
        onExpandedItemsChange={(event, itemIds) => setExpandedItems(itemIds)}
        expansionTrigger="iconContainer"
      >
        {conditionTreeItems}
      </SimpleTreeView>
    );
  }, [conditionTreeItems, expandedItems]);

  return (
    <Stack direction="row" gap={4}>
      <Box sx={{ minHeight: 400, minWidth: 600, overflowX: 'auto' }}>{conditionTreeElement}</Box>
      <Divider orientation="vertical" flexItem />
      <CriteriaFormulaPreview conditionTree={conditionTree} />
    </Stack>
  );
};

CriteriaFormulaEditor.propTypes = {
  criteria: PropTypes.array,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

CriteriaFormulaEditor.defaultProps = {
  criteria: [],
};
